/* ================================================================
   About Tin Can — founders-photo easter egg.

   The founders photo quietly swaps for a clip after N seconds on the
   page, then for a second clip N seconds after that. Notes:

   - "Seconds on the page" means seconds actually *looking* at it, so
     the clock only advances while the tab is visible. Leaving the tab
     open in the background for a minute doesn't burn the surprise.
   - Clips are fetched lazily, a few seconds before they're needed, so
     an About page view that never reaches 30s costs nothing.
   - Clicking the frame puts the founders back. That's the stop control
     for the looping clips (WCAG 2.2.2), not a documented feature.
   - Nothing runs under `prefers-reduced-motion: reduce`.
   ================================================================ */

(function () {
  'use strict';

  var TICK_MS = 500;
  var PRELOAD_LEAD_MS = 8000;
  /* A clip much smaller than the frame would be a mushy upscale, so cap
     how far it may be blown up and let it sit centred instead. */
  var MAX_UPSCALE = 2;
  /* A clip close to the frame's size fills it instead of sitting in a
     letterbox — Lemon bars around a near-fitting clip read as a broken
     image rather than a deliberate one. Past this much enlargement,
     cropping to fill costs more sharpness than the bars cost, so the
     small clips stay centred and contained. */
  var COVER_MAX_UPSCALE = 1.6;

  function initEgg(root) {
    var layers = Array.prototype.slice.call(root.querySelectorAll('[data-egg-stage]'));
    var captionBox = document.getElementById(root.getAttribute('data-egg-caption-target') || '');
    var captions = captionBox
      ? Array.prototype.slice.call(captionBox.querySelectorAll('[data-egg-caption]'))
      : [];

    /* Stage 0 is the founders photo; 1 and 2 are the clips. */
    var stages = layers
      .filter(function (el) {
        return Number(el.getAttribute('data-egg-stage')) > 0 && el.getAttribute('data-egg-src');
      })
      .sort(function (a, b) {
        return Number(a.getAttribute('data-egg-stage')) - Number(b.getAttribute('data-egg-stage'));
      });

    if (!stages.length) return;

    /* Cumulative thresholds: delay-1, then delay-1 + delay-2. */
    var thresholds = [];
    var running = 0;
    for (var i = 0; i < stages.length; i++) {
      var secs = parseFloat(stages[i].getAttribute('data-egg-delay')) || 0;
      if (secs <= 0) return;
      running += secs * 1000;
      thresholds.push(running);
    }

    var current = 0;
    var elapsed = 0;
    var last = performance.now();
    var timer = null;

    function show(stage) {
      layers.forEach(function (el) {
        el.classList.toggle('is-active', Number(el.getAttribute('data-egg-stage')) === stage);
      });
      captions.forEach(function (el) {
        el.hidden = Number(el.getAttribute('data-egg-caption')) !== stage;
      });
      current = stage;
    }

    function arm() {
      root.setAttribute('data-egg-armed', 'true');
      root.setAttribute('role', 'button');
      root.setAttribute('tabindex', '0');
      var label = root.getAttribute('data-egg-reset-label') || 'Put the photo back';
      root.setAttribute('aria-label', label);
      root.setAttribute('title', label);
    }

    function disarm() {
      root.removeAttribute('data-egg-armed');
      root.removeAttribute('role');
      root.removeAttribute('tabindex');
      root.removeAttribute('aria-label');
      root.removeAttribute('title');
    }

    function stop() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    function reset() {
      if (current === 0) return;
      stop();
      show(0);
      disarm();
    }

    function preload(el) {
      if (el.getAttribute('data-egg-loading')) return;
      el.setAttribute('data-egg-loading', '1');
      el.addEventListener(
        'load',
        function () {
          if (el.naturalWidth && el.naturalHeight) {
            var coverScale = Math.max(
              root.clientWidth / el.naturalWidth,
              root.clientHeight / el.naturalHeight
            );
            if (coverScale <= COVER_MAX_UPSCALE) {
              /* Big enough to fill the frame without going soft. */
              el.classList.add('abt-egg__layer--cover');
            } else {
              /* Centre-and-cap rather than stretch: with `inset: 0` and
                 `margin: auto`, a max-width/height below the frame size
                 centres the clip in the Lemon field. */
              el.style.maxWidth = el.naturalWidth * MAX_UPSCALE + 'px';
              el.style.maxHeight = el.naturalHeight * MAX_UPSCALE + 'px';
            }
          }
          el.setAttribute('data-egg-ready', '1');
        },
        { once: true }
      );
      el.addEventListener(
        'error',
        function () {
          /* A clip that won't load just drops out of the sequence. */
          el.setAttribute('data-egg-failed', '1');
        },
        { once: true }
      );
      el.src = el.getAttribute('data-egg-src');
    }

    function tick() {
      var now = performance.now();
      if (document.visibilityState === 'visible') elapsed += now - last;
      last = now;

      for (var i = 0; i < thresholds.length; i++) {
        var el = stages[i];
        if (el.getAttribute('data-egg-failed')) continue;
        if (elapsed >= thresholds[i] - PRELOAD_LEAD_MS) preload(el);
        if (elapsed >= thresholds[i] && current < i + 1 && el.getAttribute('data-egg-ready')) {
          show(i + 1);
          arm();
        }
      }

      /* Done once the last stage is showing (or gave up loading). */
      var lastStage = stages[stages.length - 1];
      if (current === stages.length || lastStage.getAttribute('data-egg-failed')) {
        if (current === stages.length) stop();
      }
    }

    root.addEventListener('click', reset);
    root.addEventListener('keydown', function (event) {
      if (root.getAttribute('data-egg-armed') !== 'true') return;
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        reset();
      }
    });

    timer = setInterval(tick, TICK_MS);
  }

  function init() {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    Array.prototype.forEach.call(document.querySelectorAll('[data-abt-egg]'), initEgg);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
